import { useEffect, useRef, useState } from "react"
import { Alert, Button, Stack, Typography } from "@mui/material"
import { skipToken } from "@reduxjs/toolkit/query"
import { Modal } from "@core/components/modal"
import {
  fromVersionContent,
  type TemplateWriteAccess,
} from "../../hooks/useVersionDraft"
import {
  useBulkVersionChange,
  type BulkVersionRow,
} from "../../hooks/useBulkVersionChange"
import type {
  Bindings,
  Page,
  Template,
  TemplateVersion,
} from "../../interface/publications"
import {
  useGetTemplatesQuery,
  useGetTemplateVersionsQuery,
} from "../../services/publications/templatesApi"
import {
  getActiveBindingSlots,
  getMissingActiveBindings,
  bindingUrl,
} from "../../utils/bindings"
import { FeatureErrors } from "../common/featureErrors"
import { ResourceSelect } from "../common/resourceSelect"
import { BindingValueForm } from "./bindingValueForm"
import { BulkVersionResults } from "./bulkVersionResults"
export interface BulkVersionDialogProps extends TemplateWriteAccess {
  open: boolean
  selectedIds: readonly string[]
  onClose: () => void
}
/** Keep mounted while closed. Only “Новая операция” snapshots parent selection again. */
export function BulkVersionDialog({
  selectedIds,
  onClose,
  ...access
}: BulkVersionDialogProps) {
  const bulk = useBulkVersionChange(access)
  const started = useRef(false)
  useEffect(() => {
    if (access.open && !started.current) {
      started.current = true
      void bulk.start(selectedIds)
    }
  })
  const saving = bulk.state.status === "saving"
  const writable = !access.readOnly && access.canWrite !== false
  const busy = saving || bulk.state.rows.some((row) => row.loading)
  return (
    <Modal
      title="Массовая смена версии"
      open={access.open}
      onClose={onClose}
      dialogProps={{ maxWidth: "md" }}
    >
      <Stack spacing={2}>
        <Typography>
          Только явно выбранные публикации. Меняются будущие отправки всех их
          размещений, но не старые сообщения. Неактивные значения не удаляются.
        </Typography>
        <Alert severity="info">
          Закрытие сохраняет эту операцию и её результаты. Повтор никогда не
          включает успешные публикации. «Новая операция» явно начинает работу
          заново с текущим выбором списка.
        </Alert>
        {bulk.state.validation && (
          <Alert severity="error">{bulk.state.validation}</Alert>
        )}
        <FeatureErrors error={bulk.state.error} />
        {saving && (
          <Alert severity="info">
            Сохранение… Закрытие окна не отменяет отправленный запрос.
          </Alert>
        )}
        {bulk.state.rows.map((row) => (
          <Stack
            component="section"
            role="region"
            aria-label={row.baseline?.name ?? row.publicationId}
            key={`${bulk.state.operationId}:${row.publicationId}`}
            spacing={2}
            sx={{ border: 1, borderColor: "divider", p: 2 }}
          >
            <Typography variant="h6">
              {row.baseline?.name ?? row.publicationId}
            </Typography>
            <Typography>ID публикации: {row.publicationId}</Typography>
            {row.baseline && (
              <Typography>
                Прочитанная версия: {row.baseline.templateVersionId} · Ревизия:{" "}
                {row.baseline.revision}
              </Typography>
            )}
            <BulkVersionResults row={row} />
            {row.loading && (
              <Typography>Загрузка публикации / версии…</Typography>
            )}
            {row.outcome === "draft" &&
              (!row.baseline || !row.version || !!row.error) && (
                <Button
                  disabled={busy}
                  onClick={() => void bulk.load(row.publicationId)}
                >
                  Загрузить публикацию / версию
                </Button>
              )}
            {row.baseline && row.outcome !== "success" && (
              <>
                <VersionSelection
                  row={row}
                  disabled={
                    !writable ||
                    saving ||
                    (row.outcome !== "draft" && !row.remote)
                  }
                  onSelect={(id) =>
                    void bulk.selectVersion(row.publicationId, id)
                  }
                />
                {row.version?.id === row.templateVersionId && (
                  <BindingReview
                    key={row.templateVersionId}
                    row={row}
                    disabled={
                      !writable ||
                      busy ||
                      (row.outcome !== "draft" && !row.remote)
                    }
                    onChange={(bindings) =>
                      bulk.setBindings(row.publicationId, bindings)
                    }
                  />
                )}
                {row.remote && (
                  <Button
                    disabled={!writable || busy || row.reviewed}
                    onClick={() => bulk.confirm(row.publicationId)}
                  >
                    Подтвердить предложение со свежей ревизией
                  </Button>
                )}
              </>
            )}
          </Stack>
        ))}
        {bulk.state.rows.some(
          (row) => row.outcome === "failed" || row.outcome === "unknown",
        ) && (
          <Button disabled={busy} onClick={() => void bulk.reconcile()}>
            Обновить неуспешные публикации
          </Button>
        )}
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            disabled={!bulk.canSubmit}
            onClick={() => void bulk.submit()}
          >
            Сменить версии
          </Button>
          <Button
            disabled={saving || !writable || !selectedIds.length}
            onClick={() => void bulk.start(selectedIds)}
          >
            Новая операция
          </Button>
          <Button onClick={onClose}>Закрыть</Button>
        </Stack>
      </Stack>
    </Modal>
  )
}

function BindingReview({
  row,
  disabled,
  onChange,
}: {
  row: BulkVersionRow
  disabled: boolean
  onChange: (values: Bindings) => void
}) {
  const [selected, setSelected] = useState<string>()
  const content = fromVersionContent(row.version!.content)
  const slots = getActiveBindingSlots(content)
  const missing = getMissingActiveBindings(content, row.bindings)
  const inactive = Object.keys(row.bindings).filter(
    (id) => !slots.some((slot) => slot.slotId === id),
  )
  return (
    <Stack spacing={1}>
      <Typography>Версия предложения: {row.templateVersionId}</Typography>
      <Typography>
        Недостающие активные значения: {missing.length}. Неактивные сохранённые
        значения: {inactive.length} (сохраняются).
      </Typography>
      {slots.map(({ slotId, label }, index) => (
        <Stack key={slotId}>
          <Typography>
            {label}:{" "}
            {row.bindings[slotId]?.botId !== undefined
              ? `Сохранённый legacy botId=${row.bindings[slotId].botId}; без изменений не передаётся`
              : (bindingUrl(row.bindings[slotId]) ?? "Не заполнено")}
          </Typography>
          <Button disabled={disabled} onClick={() => setSelected(slotId)}>
            Изменить ссылку {index + 1}
          </Button>
        </Stack>
      ))}
      {selected && (
        <Modal
          title="Значение подстановки"
          onClose={() => setSelected(undefined)}
        >
          <BindingValueForm
            value={row.bindings[selected]}
            disabled={disabled}
            onCancel={() => setSelected(undefined)}
            onApply={(value) => {
              if (!disabled) onChange({ [selected]: value })
              setSelected(undefined)
            }}
          />
        </Modal>
      )}
    </Stack>
  )
}

/** Accumulate only explicitly requested bounded pages, reset on query identity. */
function useOptions<T extends { id: string }>(
  key: string,
  page: number,
  current?: Page<T>,
) {
  const [loaded, setLoaded] = useState<{ key: string; options: T[] }>({
    key,
    options: [],
  })
  useEffect(() => {
    if (!current || current.page !== page) return
    setLoaded((previous) => ({
      key,
      options: [
        ...new Map(
          [
            ...(previous.key === key && page > 1 ? previous.options : []),
            ...current.data,
          ].map((item) => [item.id, item]),
        ).values(),
      ],
    }))
  }, [current, page, key])
  return loaded.key === key ? loaded.options : []
}
function VersionSelection({
  row,
  disabled,
  onSelect,
}: {
  row: BulkVersionRow
  disabled: boolean
  onSelect: (id: string) => void
}) {
  const [opened, setOpened] = useState(false)
  const [search, setSearch] = useState("")
  const [page, setPage] = useState(1)
  const [template, setTemplate] = useState<Template | null>(null)
  const query = useGetTemplatesQuery(
    opened ? { page, limit: 50, search: search || undefined } : skipToken,
  )
  const current = !query.error ? query.currentData : undefined
  const options = useOptions(search, page, current)
  return (
    <Stack spacing={1}>
      <ResourceSelect
        label="Шаблон"
        value={template}
        options={query.error ? [] : options}
        getOptionKey={(item) => item.id}
        getOptionLabel={(item) => item.name}
        onChange={(item) => {
          setTemplate(item)
          onSelect("")
        }}
        onSearch={(value) => {
          setSearch(value)
          setPage(1)
        }}
        onOpen={() => setOpened(true)}
        hasMore={!!current && page < current.totalPages}
        isFetching={query.isFetching}
        disabled={disabled}
        onLoadMore={() => {
          if (current && page < current.totalPages && !query.isFetching)
            setPage(page + 1)
        }}
        error={query.error}
        onRetry={() => void query.refetch()}
      />
      <VersionOptions
        key={template?.id ?? "none"}
        templateId={template?.id}
        selected={row.version}
        selectedId={row.templateVersionId}
        disabled={disabled || !template}
        onSelect={onSelect}
      />
    </Stack>
  )
}
function VersionOptions({
  templateId,
  selected,
  selectedId,
  disabled,
  onSelect,
}: {
  templateId?: string
  selected?: TemplateVersion
  selectedId: string
  disabled: boolean
  onSelect: (id: string) => void
}) {
  const [opened, setOpened] = useState(false)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState("")
  const query = useGetTemplateVersionsQuery(
    opened && templateId ? { templateId, page, limit: 50 } : skipToken,
  )
  const current = !query.error ? query.currentData : undefined
  const options = useOptions(templateId ?? "", page, current).filter(
    (item) => item.templateId === templateId,
  )
  const value =
    selected?.id === selectedId
      ? selected
      : (options.find((item) => item.id === selectedId) ?? null)
  return (
    <ResourceSelect
      label="Сохранённая версия"
      value={value}
      options={
        query.error
          ? []
          : options.filter((item) => String(item.number).includes(search))
      }
      getOptionKey={(item) => item.id}
      getOptionLabel={(item) => `Версия ${item.number}`}
      onChange={(item) => onSelect(item?.id ?? "")}
      onSearch={setSearch}
      onOpen={() => setOpened(true)}
      hasMore={!!current && page < current.totalPages}
      isFetching={query.isFetching}
      disabled={disabled}
      onLoadMore={() => {
        if (current && page < current.totalPages && !query.isFetching)
          setPage(page + 1)
      }}
      error={query.error}
      onRetry={() => void query.refetch()}
    />
  )
}
